import { StyledVariantList, StyledVariantListItem, StyledAlertText } from './styled';
import { Image } from '~/shared/components/Image/Image';
import styled from '@emotion/styled';
import { Link } from '~/shared/components/Link';
import { SyntheticEvent, useMemo } from 'react';
import { useRouter } from 'next/router';
import { LoaderSpinner } from '~/features/product-details-page/styled';
import { VariantPickerProps, VariationAttributesValues } from '../PDPVariants';
import { toast } from 'react-toastify';
import useRedirectToBestMatch from '~/features/product-details-page/hooks/useRedirectToBestMatch';
import { useTranslation } from '~/shared/utils/translation';

const SoldOutWrap = styled.div(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    svg: {
        height: '100%',
        width: '100%',
        margin: 0,
    },
}));

type VariantListProps = VariantPickerProps;

const VariantList = ({ variant, onClick, id, selectedAttributes, isLoading }: VariantListProps) => {
    const hasMoreThanBaseVariant = variant.values?.length > 1;
    const router = useRouter();
    const { redirectToBestMatch } = useRedirectToBestMatch();
    const { translate } = useTranslation();

    const handleClick = (event: SyntheticEvent, value: VariationAttributesValues) => {
        event.preventDefault();

        if (!onClick) return;

        if (value?.href) {
            return router.replace(value?.href, undefined, { shallow: true });
        }

        onClick({ ...value, name: id });

        if (!value?.id) {
            toast(
                <StyledAlertText>
                    {translate('pdp.variant.changeSelectionTitle') && (
                        <>
                            <strong>{translate('pdp.variant.changeSelectionTitle')}</strong>
                            <br />
                        </>
                    )}

                    {translate('pdp.variant.changeSelectionDescription')}
                </StyledAlertText>,
                {
                    hideProgressBar: true,
                    autoClose: 5000,
                    pauseOnHover: false,
                    closeOnClick: true,
                    theme: 'dark',
                    style: { width: 300, marginLeft: 'auto', alignItems: 'flex-start' },
                    bodyStyle: { alignItems: 'flex-start' },
                    containerId: 'toastvariantcontainer',
                    toastId: 'varianterrorpopup',
                },
            );

            redirectToBestMatch({ ...selectedAttributes, [id]: value.value }, id);
        }
    };

    const sortedVariants = useMemo(
        () =>
            [...variant.values].sort((a, b) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
            }),
        [variant],
    );

    return (
        <StyledVariantList>
            {selectedAttributes &&
                hasMoreThanBaseVariant &&
                sortedVariants.map((value) => (
                    <Link
                        href={value?.href}
                        key={value.value}
                        onClick={(event) => handleClick(event, value)}
                    >
                        <StyledVariantListItem
                            isActive={value.value === selectedAttributes[id]}
                            data-value={value}
                        >
                            {value.value === selectedAttributes[id] && isLoading && (
                                <LoaderSpinner />
                            )}
                            {value?.image ? (
                                <Image
                                    disableSkeleton
                                    width={42}
                                    height={42}
                                    layout="fixed"
                                    objectFit="cover"
                                    src={value.image?.url}
                                    alt={value.image?.alt || value.name}
                                />
                            ) : (
                                value.name
                            )}
                            {value?.disabled && (
                                <SoldOutWrap>
                                    <svg
                                        width="46"
                                        height="46"
                                        viewBox="0 0 46 46"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clip-path="url(#clip0_1_2)">
                                            <rect width="46" height="46" fill="none" />
                                            <line
                                                x1="-1.35726"
                                                y1="47.6502"
                                                x2="45.6427"
                                                y2="-0.349813"
                                                stroke="#8E8E8D"
                                            />
                                            <line
                                                x1="-1.64296"
                                                y1="-2.35004"
                                                x2="48.357"
                                                y2="48.65"
                                                stroke="#8E8E8D"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_1_2">
                                                <rect width="46" height="46" fill="none" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </SoldOutWrap>
                            )}
                        </StyledVariantListItem>
                    </Link>
                ))}
        </StyledVariantList>
    );
};

export default VariantList;
